import { ContactType } from '@api/generated/graphql';
import { NotificationsCollapsible } from '@components/NotificationsCollapsible/NotificationsCollapsible';
import maskPhoneInput from '@lib/utils/maskPhoneInput';
import {
  Card,
  Form,
  Stack,
  StackItem,
  Tooltip,
} from '@servicetitan/design-system';
import { inboxLocators } from '@locators/inbox';
import { ChangeEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import strings from '../../strings';
import ContactMethodIconMenu from './ContactMethodIconMenu';
import { RemoveButton } from './ContactMethodItem.styled';
import {
  ContactFormPrefixes,
  ContactMethodOptions,
} from '../../ContactMethodDefinitions';
import { CreateCustomerFormValues } from '@components/CreateCustomer/components/CreateCustomerForm/CreateCustomerFormDefinitions';
import { EditContactsValidationType } from '@pages/agent/InboxPage/components/ContactsDrawers/validations';
import { getTestIdProps } from '@lib/testUtils';

export type ContactMethodItemProps = {
  formFieldPrefix: ContactFormPrefixes;
  contactMethodType: ContactType;
  index: number;
  onBlur: (index: number) => void;
  onRemove: (index: number) => void;
  onChangeType: (type: ContactType, index: number) => void;
  isContactTypeMenuOpen: boolean;
  onContactTypeMenuOpenChange: (open: boolean) => void;
};

/**
 * Renders a contact method item for ContactMethods.
 *
 * @param formFieldPrefix - The prefix for the form field names.
 * @param contactMethodType - The contact method object to be rendered.
 * @param index - The index of the contact method item.
 * @param onRemove - The callback function to remove the contact method.
 * @param onChangeType - The callback function to change the contact method type.
 */
export function ContactMethodItem({
  formFieldPrefix,
  contactMethodType,
  index,
  onRemove,
  onChangeType,
  onBlur,
  isContactTypeMenuOpen,
  onContactTypeMenuOpenChange,
}: ContactMethodItemProps) {
  const {
    formState: { errors },
    getValues,
    setValue,
    register,
  } = useFormContext<CreateCustomerFormValues & EditContactsValidationType>();

  const isPhoneOrMobileContactMethod =
    contactMethodType === ContactType.Phone ||
    contactMethodType === ContactType.MobilePhone;
  const isNumberMethod = contactMethodType !== ContactType.Email;

  // handle the change of the contact method value
  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = isNumberMethod
      ? maskPhoneInput(e.target.value)
      : e.target.value;
    if (isNumberMethod) {
      const maskedPhone = maskPhoneInput(e.target.value);
      e.target.value = maskedPhone;
    }
    setValue(`${formFieldPrefix}.contacts.${index}.value`, newValue);
  };

  return (
    <Card thin className='border-style-none m-b-2'>
      <Card.Section>
        <Stack spacing={1}>
          <StackItem className='w-50'>
            <Form.Input
              small
              {...getTestIdProps(
                inboxLocators.customerSearch.editFormFields[contactMethodType],
              )}
              placeholder={strings.placeholders[contactMethodType]}
              label={ContactMethodOptions[contactMethodType].label}
              error={errors[formFieldPrefix]?.contacts?.[index]?.value?.message}
              defaultValue={getValues(
                `${formFieldPrefix}.contacts.${index}.value`,
              )}
              shortLabel={
                <ContactMethodIconMenu
                  isPhoneOrMobile={isPhoneOrMobileContactMethod}
                  iconType={ContactMethodOptions[contactMethodType].icon}
                  onChangeType={onChangeType}
                  index={index}
                  isContactTypeMenuOpen={isContactTypeMenuOpen}
                  onContactTypeMenuOpenChange={onContactTypeMenuOpenChange}
                />
              }
              {...register(`${formFieldPrefix}.contacts.${index}.value`)}
              onChange={handleValueChange}
              onBlur={() => onBlur(index)}
            />
          </StackItem>
          <StackItem className='w-50'>
            <Form.Input
              label={strings.labels.memo}
              placeholder={strings.placeholders.memo}
              small
              defaultValue={getValues(
                `${formFieldPrefix}.contacts.${index}.memo`,
              )}
              {...register(`${formFieldPrefix}.contacts.${index}.memo`)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValue(
                  `${formFieldPrefix}.contacts.${index}.memo`,
                  e.target.value,
                )
              }
              onBlur={() => onBlur(index)}
            />
          </StackItem>
          <StackItem>
            <Tooltip text={strings.labels.removeContactMethod} portal>
              <RemoveButton
                aria-label={strings.labels.removeContactMethod}
                iconName='delete'
                fill='subtle'
                className='c-neutral-100'
                onClick={onRemove}
              />
            </Tooltip>
          </StackItem>
        </Stack>
      </Card.Section>
      <Card.Section className='bg-neutral-20'>
        <NotificationsCollapsible
          formFieldPrefix={formFieldPrefix}
          index={index}
        />
      </Card.Section>
    </Card>
  );
}

export default ContactMethodItem;
